import React, { useState, useRef, useEffect } from "react";
import { regExpGroups, regExpNoDate } from "./data/regularExpForTime";
import useSetDataRecognition from "./data/useSetDataRecognition";
import { RepeatTemplateTaskData, TagFromTagsList } from "../Types/Types";
import { useDispatch, useSelector } from "react-redux";
import MTagsApp from "../DropDownMenu/SelectTags/TagsApp";
import { RootState } from "../store";
import { getTagsList } from "../DropDownMenu/SelectTags/actions/getTagsList";

export interface SmartRecognitionProps {
    setDate: Function,
    setTime: Function,
    setTaskHours: Function,
    setTaskMinutes: Function,
    setTitle: Function,
    setPriority: Function,
    updateRepeatTemplate: Function,
    setAutoFocus: Function,
    checkLengthOfTitle: Function,
    updateTagList: Function,
    tagList: TagFromTagsList[],
    setTagList: Function,
    removeTagFromTagList: Function,
    arrMatching: string[],
    setArrMatching: Function,
    priority: number,
    selectedValues: {
        priority: number | null,
        time: string | null,
        date: string | Date | null ,
        timeToTaskHours: string | null,
        timeToTaskMinutes: string | null,
        repeatTemplate: RepeatTemplateTaskData
    },
    datePayload: Date | null
}

let counter = 0;

export default function SmartRecognition({setTime, setDate, setTaskHours, setTaskMinutes, setPriority, updateRepeatTemplate, selectedValues, datePayload, checkLengthOfTitle, setAutoFocus, updateTagList, tagList, setTagList, removeTagFromTagList, setTitle, priority} : SmartRecognitionProps) {
    const [content, setContent] = useState<string>('');
    const dispatch = useDispatch();
    const refSmartRecognition = useRef<(HTMLDivElement)>(null);
    const [spanElements, setSpanElements] = useState<Element[]>([]);
    const [isTagsListOpen, setIsTagsListOpen] = useState<boolean>(false);
    const { tagsList } = useSelector((state: RootState) => state.tagsNew)
    const [tagPayload, setTagPayload] = useState<string>('');
    const { setDataRecognitions } = useSetDataRecognition(spanElements, setPriority, updateRepeatTemplate, setDate, setTime, setTaskHours, setTaskMinutes, selectedValues, datePayload)
    
    const setSpanTracking = (span: HTMLSpanElement): void => { //tracking span
        span.setAttribute('data-tracking', 'true');
        span.addEventListener('click', removeContentEditable);
        span.setAttribute('class', 'MatchedText');
    }

    const setSpanUntracking = (span: HTMLSpanElement): void => { //untracking span                
        span.setAttribute('data-tracking', 'false')
        span.removeAttribute('class')
        span.removeEventListener('click', removeContentEditable)
        setSpanElements(prevState => [...prevState])
    }
    
    const addSpanContainer = (content: string, groupName: string): HTMLSpanElement => { //surround text into span
        const spanContainer = document.createElement('span');
        spanContainer.textContent = `${content}`;
        spanContainer.setAttribute('data-match-id', content);
        spanContainer.setAttribute('data-match-group', groupName);
        spanContainer.className = 'MatchedText';
        spanContainer.setAttribute('data-tracking', 'true')
        spanContainer.addEventListener('click', removeContentEditable)
        return spanContainer
    }
    
    const handleInput = () => { //input text handler
        if (refSmartRecognition.current) {
            const originalText = refSmartRecognition.current.innerText.replace(/&nbsp;/gi, ' ');
            setContent(originalText);
            const currentSpans = refSmartRecognition.current?.querySelectorAll('span');
            currentSpans?.forEach((span: Element) => {
            if ((span.contains(document?.getSelection()?.anchorNode ?? null)) &&  (span.getAttribute('data-match-group') === 'TaskTags')) {
                setTagPayload(span.textContent?.slice(1) ?? '');
            }})
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {  //paste text handler
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text/plain');
        refSmartRecognition.current?.insertAdjacentText("beforeend", pastedText);
        const nodeRange = new Range();
        let hasMatch = true;
        while (hasMatch) {
            hasMatch = false;
            // eslint-disable-next-line 
            refSmartRecognition.current?.childNodes.forEach((Node) => {
                const textNode = Node.textContent ?? '';
                if (Node.nodeName === '#text' && Node.nodeValue !== ' ') {
                    for (const [groupName, regexpArr] of Object.entries(regExpGroups)) {
                        for (const elRegExp of regexpArr) {
                            const matches = [...textNode.matchAll(elRegExp)];
                            if (matches.length > 0) {
                                const lastMatch = matches[matches.length - 1];
                                const newSpan = addSpanContainer(lastMatch[0], groupName);                        
                                nodeRange.setStart(Node, lastMatch?.index!);
                                nodeRange.setEnd(Node, lastMatch[0].length + lastMatch.index!);
                                nodeRange.surroundContents(newSpan);
                                Node.textContent?.trim();
                                hasMatch = true;
                                setSpanElements(prevState => [...prevState, newSpan]);                            
                                checkDublicateSpans(groupName as keyof typeof regExpGroups);
                                return;
                            }                            
                        }
                    }
                }
            })
        }
        moveCaretToEnd(refSmartRecognition.current as HTMLDivElement);
        setTitle(cleaningUpTitle());
    }
    
    const cleaningUpTitle = (): string => { //cleaning entered text before saving
        const nodes = refSmartRecognition.current?.childNodes;
        const textNodes: string[] = []
        nodes?.forEach((el: ChildNode) => {
            if (el.nodeType === 1) {
                const sp: Element = el as HTMLSpanElement
                if (sp.getAttribute('data-tracking') === 'false') {
                    textNodes.push(el.textContent?.trim() ?? " ")
                }
            }          
            if (el.nodeName === '#text' && el.textContent !== "") {
                textNodes.push(el.textContent?.trim() ?? " ")
            }
        })
        const finallyText = textNodes.join(' ').replace('  ', ' ').replace(/&nbsp;/gi, ' ')
        return finallyText
    }

    const checkDublicateSpans = (groupName: keyof typeof regExpGroups): void => { //checking dublicate spans, last right element - is first
        if (groupName === 'TaskTags') {
            return
        }
        const dublicateSpans = refSmartRecognition.current?.querySelectorAll<HTMLSpanElement>(`span[data-match-group="${groupName}"`) ?? []
        if (dublicateSpans.length > 1) {
            dublicateSpans.forEach((span, index) => {
                if (index < dublicateSpans.length - 1) {
                    setSpanUntracking(span)
                } else if (index === dublicateSpans.length - 1){
                    setSpanTracking(span)
                }
            })
        }
    }

    useEffect(()=> { //remove span NoDate if entered time to task
        const hasTime = spanElements.findIndex(el => el.getAttribute('data-tracking') === 'true' && el.getAttribute('data-match-group') === 'TaskTime')
        const hasNoDate = spanElements.findIndex(el => el.textContent?.match(regExpNoDate))        
        if (hasTime !== -1 && hasNoDate !== -1) {
            if (hasTime > hasNoDate) {
                setSpanUntracking(spanElements[hasNoDate] as HTMLSpanElement)
            } else if (hasNoDate > hasTime) {
                setSpanUntracking(spanElements[hasTime] as HTMLSpanElement)
            }
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */          
    }, [spanElements])

    const moveCaretToEnd = (htmlElement: HTMLDivElement | HTMLSpanElement): void => { //moving caret to div block end      
        const range = document.createRange();
        range.selectNodeContents(htmlElement);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        htmlElement.focus();
        range.detach();        
    }

    const addSpaceBeforeSpan = (htmlElement: HTMLSpanElement): void => {
        const space = document.createTextNode("\u00A0");
        const rrr = htmlElement.parentNode
        rrr?.insertBefore(space, htmlElement.nextSibling)
        moveCaretToEnd(refSmartRecognition.current as HTMLDivElement)
    }

    const removeContentEditable = (e: MouseEvent): void => { //removing span surrounds
        e.stopPropagation()   
        const value = e.target as HTMLSpanElement
        const span = refSmartRecognition.current?.querySelectorAll(`span[data-match-group="${value.attributes[1].value}"]`) ?? [] 
        span?.forEach(el => {            
            if (el.getAttribute('data-tracking') === 'true') {
                if (value.attributes[1].value === 'TaskTags') {
                    const getSpanCurrentTag= el.getAttribute('data-match-id')?.slice(1)
                    const getTagId = tagsList.find(el => el.tagName === getSpanCurrentTag)                        
                    removeTagFromTagList(getTagId?.id)
                }
                // if (value.attributes[1].value === 'TaskRepeat') {
                //     console.log('ddddddddddddddd');
                    
                //     setDate(new Date().toLocaleDateString())
                // }
                setSpanUntracking(el as HTMLSpanElement)
                const nextSpanIndex = Array.from(span).indexOf(el);
                if (nextSpanIndex > 0 && nextSpanIndex < span.length) {
                    setSpanTracking(span[nextSpanIndex - 1] as HTMLSpanElement)
                    moveCaretToEnd(span[nextSpanIndex - 1] as HTMLSpanElement)
                }
            }
        })
        
        setTitle(cleaningUpTitle())
    }       

    useEffect(() => {  //removing span if entered text not match by id
        spanElements?.forEach((span) => {
            if (span.nodeName === 'SPAN' && span.contains(document?.getSelection()?.anchorNode ?? null)) {
                const textNode = document.createTextNode(span.textContent || '')
                const spanRange = new Range()
                if (span.textContent !== span.getAttribute('data-match-id')) {
                    setSpanUntracking(span as HTMLSpanElement)
                    spanRange.selectNode(span)
                    spanRange.deleteContents()
                    spanRange.insertNode(textNode)
                    spanRange.collapse(false);
                    const selection = window.getSelection();
                    selection?.removeAllRanges();
                    selection?.addRange(spanRange);
                    spanRange.detach();
                } else {
                    setSpanTracking(span as HTMLSpanElement)
                }
            }
        })
            /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [refSmartRecognition.current?.textContent])

    useEffect(() => { //cleaning document from empty nodes
        refSmartRecognition.current?.childNodes.forEach(el => {
            if ((el.nodeName === '#text' && el.nodeValue === '') || (el.nodeName === 'BR')) {
                el.remove()
            }
        })
    })

    useEffect(() => { //tracking entered text by regExp       
        const divChilds = refSmartRecognition.current?.childNodes        
        const nodeRange = new Range();
        divChilds?.forEach((Node) => {
            if (Node.nodeName === '#text' && Node.nodeValue !== ' ') {
                const textNode = Node.textContent ?? ''                
                for (const [groupName, regexpArr] of Object.entries(regExpGroups)) {
                    for (const elRegExp of regexpArr) {
                        const matches = [...textNode.matchAll(elRegExp)]
                        if (matches.length > 0) {
                            const lastMatch = matches[matches.length - 1]
                            let newSpan: HTMLSpanElement;
                            if (groupName === 'TaskTags') {
                                newSpan = addSpanContainer(lastMatch[0], groupName)
                                setIsTagsListOpen(true)
                                if (counter === 0) {
                                    dispatch(getTagsList())
                                }
                            } else {
                                newSpan = addSpanContainer(lastMatch[0], groupName)
                            }
                            nodeRange.setStart(Node, lastMatch?.index!)
                            nodeRange.setEnd(Node, lastMatch[0].length + lastMatch.index!)
                            nodeRange.surroundContents(newSpan)
                            Node.textContent?.trim()
                            moveCaretToEnd(newSpan)
                            setSpanElements(prevState => [...prevState, newSpan])                            
                            checkDublicateSpans(groupName as keyof typeof regExpGroups)
                            return
                        }
                    }
                }
            }
        })
        setTitle(cleaningUpTitle())
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [content])
    
    

    useEffect(() => { //calling custom hook from changing an array of span elements        
        setDataRecognitions()
        
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [spanElements])   
    
    useEffect(() => { //replacement span node if its contents are not equal to its id with text node
        const currentSpans = refSmartRecognition.current?.querySelectorAll('span');
        const range = new Range();
        currentSpans?.forEach((span: Element) => {
            if (span.contains(document?.getSelection()?.anchorNode ?? null)){
                if (span.textContent !== span.getAttribute('data-match-id')) {
                    if (span.getAttribute('data-match-group') === 'TaskTags') {
                        const getTagId = tagsList.find(el => el.tagName === span.getAttribute('data-match-id')?.slice(1))
                        removeTagFromTagList(getTagId?.id)
                    }
                    const textNode = document.createTextNode(`${span.textContent}`)
                    refSmartRecognition.current?.replaceChild(textNode, span)
                    const selection = window.getSelection();
                    range.setStart(textNode, textNode.length);
                    range.collapse(true);
                    selection?.removeAllRanges();
                    selection?.addRange(range);
                }
            }
        })
    })

    const keyHandler = (e: KeyboardEvent): void  => { //key press handler
        const currentSpans = refSmartRecognition.current?.querySelectorAll('span');
        currentSpans?.forEach((span: Element) => {
            if (span.contains(document?.getSelection()?.anchorNode ?? null)){
                if (e.code === 'Backspace') {
                    if (!e.repeat) {
                        if ((span.getAttribute('data-tracking') === 'true') && span.getAttribute('data-match-group') === 'TaskTags') {
                            const getSpanCurrentTag= span.getAttribute('data-match-id')?.slice(1)
                            // if (span.innerHTML.length === 1) {
                            //     if (isTagsListOpen) {
                            //         setIsTagsListOpen(false)
                            //     }
                            // } else {
                                const getTagId = tagsList.find(el => el.tagName === getSpanCurrentTag)
                                removeTagFromTagList(getTagId?.id)                           
                            //     return
                            // }
                            setIsTagsListOpen(false)
                            e.preventDefault()
                            setTagPayload('')
                            setSpanUntracking(span as HTMLSpanElement)
                        }
                        if (span.getAttribute('data-tracking') === 'true') {
                            e.preventDefault()
                            setSpanUntracking(span as HTMLSpanElement)
                        }

                        const twinSpans = refSmartRecognition.current?.querySelectorAll(`span[data-match-group="${span.getAttribute('data-match-group')}"]:not([data-match-group="TaskTags"])`) ?? []                        
                        
                        if (twinSpans?.length > 1) {
                            const nextSpanIndex = Array.from(twinSpans).indexOf(span);
                            if (nextSpanIndex > 0 && nextSpanIndex < twinSpans.length) {
                                setSpanTracking(twinSpans[nextSpanIndex - 1] as HTMLSpanElement)
                                moveCaretToEnd(twinSpans[nextSpanIndex - 1] as HTMLSpanElement)
                            }
                        }
                        setTitle(cleaningUpTitle())                        
                    }
                } else if (e.code === 'Space') {
                    if (isTagsListOpen) {
                        setIsTagsListOpen(false)
                    }
                    if ((span.getAttribute('data-match-group') === 'TaskTags') && (span.classList[0] !== 'MatchedText')) {
                        // e.preventDefault()
                        setSpanUntracking(span as HTMLSpanElement)
                    }
                    span.setAttribute("contentEditable", "false")
                    moveCaretToEnd(refSmartRecognition.current as HTMLDivElement);
                    setTimeout(() => {
                        span.setAttribute("contentEditable", "true")
                    }, 0);
                } if (e.code === 'Enter') {
                    e.preventDefault()
                } if (e.code === 'Escape') {
                    e.stopPropagation()
                    if (isTagsListOpen) {
                        setIsTagsListOpen(false)
                        setSpanUntracking(span as HTMLSpanElement)
                    }
                }
            }
        })
        if (e.code === 'Escape') {
            e.stopPropagation()
            if (isTagsListOpen) {
                setIsTagsListOpen(false)
            }
        }
        if (e.code === 'Delete') {            
            const selection = window.getSelection();
            if (!selection?.rangeCount) return;
            const range = selection?.getRangeAt(0);
            const beforeNode = range.startContainer;
            if (beforeNode.nodeType === Node.TEXT_NODE) {
                const parentElement = beforeNode;
                if (parentElement && parentElement.nextSibling && parentElement.nextSibling?.nodeName === 'SPAN') {
                    const span = parentElement.nextSibling;
                    setSpanUntracking(span as HTMLSpanElement)
                }
            }
        } 
        if (e.code === 'Backspace') {
            const getSelection = window.getSelection()
            if (!getSelection?.isCollapsed) {
                setDate(new Date().toLocaleDateString())
                setTime('')
                setPriority(3)
                setTaskHours('')
                setTaskMinutes('')
                setSpanElements([])
            }
        }
    }

    const handleAddTag = (e: KeyboardEvent | MouseEvent, payload: TagFromTagsList) => {
        updateTagList(e, {tagId: payload.tagId, tagName: payload.tagName})
        setIsTagsListOpen(false);
        const currentSpans = refSmartRecognition.current?.querySelectorAll('span')
        currentSpans?.forEach((span: Element) => {
            switch (e.type) {
                case 'click':
                    if ((span.getAttribute('data-match-id') === '@') && (span.getAttribute('data-tracking') === 'true')){                
                        setTimeout(() => {
                            span.textContent = `@${payload.tagName}`;
                            span.setAttribute('data-match-id', payload.tagName);
                            // moveCaretToEnd(span as HTMLSpanElement)
                            addSpaceBeforeSpan(span as HTMLSpanElement)             
                        }, 50);
                    }
                    break;
                case 'keydown':
                    if (span.contains(document?.getSelection()?.anchorNode ?? null)){                
                        setTimeout(() => {
                            span.textContent = `@${payload.tagName}`;
                            span.setAttribute('data-match-id', payload.tagName);
                            span.setAttribute("contentEditable", "true")
                            // moveCaretToEnd(span as HTMLSpanElement)
                        }, 50);
                        addSpaceBeforeSpan(span as HTMLSpanElement)             
                    }
                    break;
            }
            
        })
        setTagPayload('')
    }

    return (
        <div>
            <div id="smartRecognition"
                contentEditable
                data-placeholder="Название задачи"
                ref={refSmartRecognition}
                className="add-task__input add-task__input-title"
                onInput={handleInput}
                onPaste={(e) => handlePaste(e)}
                onKeyDown={(e) => keyHandler(e as any)}
                />
            {
                isTagsListOpen &&
                    <div style={{position: 'absolute', zIndex: 1, width: '100%', maxWidth: '697px', maxHeight: '130px', height: 'auto', overflow: "hidden", overflowY: 'hidden', backgroundColor: '#FFF', border: '1px solid #E8E8E8', borderRadius: '10px', boxShadow: '0px 0px 25px 0px #23236026'}}>
                        <MTagsApp
                        isHideCheckedItems={true}
                            updateTagList={handleAddTag}
                            removeTagFromTagList={removeTagFromTagList}
                            tagsInList={tagList}
                            showCheckbox={false}
                            modalShow={false}
                            matchRegExp={tagPayload}
                        />
                    </div>
            }
        </div>
    )
}

